import type { HeadMeta, HeadMetaDynamicTypes, Breadcrumb } from '@/types/HeadMeta'

// Constants
const META_DESCRIPTION_LENGTH = 160
const socialImagesLink = 'https://growni.sk/images/social/'

/**
 * Composable for managing head meta information
 */
export function useHeadMeta() {
    // Helper functions
    function getFileExtension(filename: string) {
        return removeParams(filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2))
    }

    // Get clean URL, remove everything after question mark
    function removeParams(value: string) {
        return value.split('?')[0]
    }

    // Getter for data via Slug
    function headMetaLookup(slug: string) {
        return getHeadMetaData().find((x) => x.slugId === slug)
    }

    // Function to check if it is titleTemplate syntax like in vue-meta, used in dynamic meta tags
    function headMetaHasTitleTemplate(title: string) {
        return title.includes('%s')
    }

    // Function to simulate titleTemplate like in vue-meta, used in dynamic meta tags
    function headMetaTitleTemplate(title: string, stringToReplace: string) {
        return title ? title.replace('%s', stringToReplace) : ''
    }

    // Cleanup description, if there is HTML syntax
    function headMetaDescription(description: string) {
        let richText = description.replace(/<[^>]+>/g, '')
        if (richText && richText.length > META_DESCRIPTION_LENGTH) {
            richText = richText.slice(0, META_DESCRIPTION_LENGTH - 4) + ' ...'
        }
        return richText || description
    }

    function headMetaTrackGTM(trackData: string) {
        return {
            script: [
                {
                    innerHTML: `window.dataLayer = window.dataLayer || []; window.dataLayer.push({${trackData}});`,
                    type: 'text/javascript' as const
                }
            ],
            __dangerouslyDisableSanitizers: ['script']
        }
    }

    function getSlugByBreadcrumbTitle(title: string) {
        return getHeadMetaData().find((x) => x?.breadcrumbs?.title === title)?.slugId
    }

    function getMetaBreadCrumbs(route: any): Breadcrumb {
        const data: HeadMeta | undefined = headMetaLookup(route.name ?? '')
        return data?.breadcrumbs as Breadcrumb
    }

    function getHeadMeta(route: any, dynamic?: HeadMetaDynamicTypes) {
        let data: HeadMeta | undefined = headMetaLookup(route.name ?? '')

        if (data && dynamic?.title) {
            data = {
                ...data,
                title: dynamic.title
            }
        }

        if (data && data?.meta) {
            data.meta = [
                ...data.meta,
                {
                    hid: 'og:url',
                    property: 'og:url',
                    content: 'https://growni.sk' + route.fullPath
                }
            ]

            if (dynamic?.title) {
                const hasTemplate = headMetaHasTitleTemplate(data.titleTemplate as string)
                data.meta = [
                    ...data.meta,
                    ...getHeadMetaOpenGraphData(
                        'title',
                        hasTemplate
                            ? headMetaTitleTemplate(data.titleTemplate as string, dynamic.title)
                            : dynamic.title,
                        true
                    )
                ]
            }

            if (dynamic?.description) {
                dynamic.description = headMetaDescription(dynamic.description)
                data.meta = [...data.meta, ...getHeadMetaOpenGraphData('description', dynamic.description)]
            }

            if (dynamic?.image) {
                const imageExtension = getFileExtension(dynamic.image)
                data.meta = [
                    ...data.meta,
                    ...getHeadMetaOpenGraphData('image', dynamic.image, true),
                    ...getHeadMetaOpenGraphData('image:type', 'image/' + imageExtension, true)
                ]
            }

            if (dynamic?.imageAlt) {
                const hasTemplate = headMetaHasTitleTemplate(dynamic.imageAlt)
                data.meta = [
                    ...data.meta,
                    ...getHeadMetaOpenGraphData(
                        'image:alt',
                        hasTemplate
                            ? headMetaTitleTemplate(dynamic.imageAlt, dynamic.title as string)
                            : dynamic.imageAlt,
                        true
                    )
                ]
            }
        }

        // Add dataLayer Google Tag Manager page parameters
        if ((data && data?.gtm) || (data && dynamic?.gtm)) {
            let dataLayerHtml = `event: "virtual_page_view",
          page_type: "${dynamic?.gtm?.page_type || data.gtm?.page_type}",
          modul_type: "${dynamic?.gtm?.modul_type || data.gtm?.modul_type}",
          has_cta: ${!!(dynamic?.gtm?.has_cta || data.gtm?.has_cta)}`

            // There has to be added unique_id because of tracking between same sub-pages
            dataLayerHtml = dataLayerHtml + `, unique_id: "${dynamic?.gtm?.unique_id || data.gtm?.unique_id}"`

            if (dynamic?.gtm?.modul_subtype) {
                const modulSubtype = Array.isArray(dynamic?.gtm?.modul_subtype)
                    ? dynamic?.gtm?.modul_subtype.sort().join(',')
                    : dynamic?.gtm?.modul_subtype
                dataLayerHtml = dataLayerHtml + `, modul_subtype: "${modulSubtype}"`
            }

            data = {
                ...data,
                ...headMetaTrackGTM(dataLayerHtml)
            }
        }

        const { slugId: _slugId, gtm: _gtm, ...metaInfo } = data! || {}

        return metaInfo
    }

    // Function to generate og: and twitter: meta tags
    function getHeadMetaOpenGraphData(type: string, contentText: string, onlyOg: boolean = false) {
        const og: NonNullable<HeadMeta['meta']> = [
            {
                hid: 'og:' + type,
                property: 'og:' + type,
                content: contentText
            },
            {
                hid: 'twitter:' + type,
                name: 'twitter:' + type,
                content: contentText
            }
        ]

        if (!onlyOg) {
            og.push({
                hid: type,
                name: type,
                content: contentText
            })
        }
        return og
    }

    // Return all the functions and data needed externally
    return {
        headMetaDescription,
        headMetaTitleTemplate,
        headMetaTrackGTM,
        getSlugByBreadcrumbTitle,
        getMetaBreadCrumbs,
        getHeadMeta,
        getHeadMetaOpenGraphData
    }
}

function getHeadMetaData(): HeadMeta[] {
    // For now, we'll use a simplified approach and import the data directly
    // This should be refactored to use the proper service pattern from HeadMetaService.ts
    return [
        {
            slugId: 'index',
            title: 'GROWNi.sk – mentoring, dobrovoľníctvo, práca a stáže',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'GROWNi prepája ľudí s mentormi, dobrovoľníckymi projektmi, prácou a stážami aj vzdelávaním. Nájdi príležitosť, ktorá ťa posunie ďalej.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'GROWNi.sk – mentoring, dobrovoľníctvo, práca a stáže'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'GROWNi prepája ľudí s mentormi, dobrovoľníckymi projektmi, prácou a stážami aj vzdelávaním. Nájdi príležitosť, ktorá ťa posunie ďalej.'
                }
            ],
            gtm: {
                page_type: 'homepage',
                modul_type: 'other'
            }
        },
        {
            slugId: 'kruzky',
            title: 'Krúžky — mesačné stretnutia pre osobný rast | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Krúžky sú 10-mesačný program LEAF: skupina 8–10 ľudí sa raz mesačne stretáva na štyri hodiny so školeným moderátorom. Priestor na sebareflexiu, dôveru a rast.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Krúžky — mesačné stretnutia pre osobný rast | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Krúžky sú 10-mesačný program LEAF: skupina 8–10 ľudí sa raz mesačne stretáva na štyri hodiny so školeným moderátorom. Priestor na sebareflexiu, dôveru a rast.'
                }
            ],
            breadcrumbs: {
                title: 'Krúžky'
            }
        },
        {
            slugId: 'kruzky-clenske',
            title: 'Členský príspevok | GROWNi.sk',
            meta: [
                { hid: 'description', name: 'description', content: 'Členský príspevok do komunity LEAF.' },
                { hid: 'og:description', property: 'og:description', content: 'Členský príspevok do komunity LEAF.' },
                { hid: 'og:title', property: 'og:title', content: 'Členský príspevok | GROWNi.sk' }
            ],
            breadcrumbs: {
                title: 'Členský príspevok'
            }
        },
        {
            slugId: 'vzdelavanie',
            title: 'Workshopy, kurzy, webináre a podujatia | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Workshopy, kurzy, webináre a podujatia pre tvoj profesijný aj osobný rast, ale aj študijné a štipendijné príležitosti. Vyber si podľa témy a termínu.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Workshopy, kurzy, webináre a podujatia | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Workshopy, kurzy, webináre a podujatia pre tvoj profesijný aj osobný rast, ale aj študijné a štipendijné príležitosti. Vyber si podľa témy a termínu.'
                }
            ],
            breadcrumbs: {
                title: 'Vzdelávanie'
            }
        },
        {
            slugId: 'mcp-connect',
            title: 'Pripoj GROWNi k svojmu AI asistentovi | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Pripoj svoj GROWNi účet k AI asistentom ako Claude cez MCP server. Vyhľadávaj mentorov, prácu a dobrovoľníctvo a spravuj nábor svojej organizácie priamo z AI asistenta.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Pripoj GROWNi k svojmu AI asistentovi | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Pripoj svoj GROWNi účet k AI asistentom ako Claude cez MCP server. Vyhľadávaj mentorov, prácu a dobrovoľníctvo a spravuj nábor svojej organizácie priamo z AI asistenta.'
                }
            ],
            breadcrumbs: {
                title: 'Pripojiť AI asistenta'
            }
        },
        {
            slugId: 'vzdelavanie-slug',
            titleTemplate: '%s | GROWNi.sk',
            breadcrumbs: {
                title: '%s',
                path: ['Vzdelávanie']
            }
        },
        {
            slugId: 'mentori',
            title: 'Mentori z praxe: nájdi si svojho mentora | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Nájdi si mentora alebo mentorku z praxe podľa oblasti, mesta a témy, ktorú práve riešiš. Prezri si profily a požiadaj o mentoring priamo cez GROWNi.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Mentori z praxe: nájdi si svojho mentora | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Nájdi si mentora alebo mentorku z praxe podľa oblasti, mesta a témy, ktorú práve riešiš. Prezri si profily a požiadaj o mentoring priamo cez GROWNi.'
                }
            ],
            breadcrumbs: {
                title: 'Mentori',
                path: ['Mentoring']
            }
        },
        {
            slugId: 'mentoring',
            title: 'Mentoring: čo to je a ako nájsť mentora | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Mentoring na GROWNi prepája mentees s mentormi z praxe. Zisti, čo je mentoring, ako sa líši od koučingu a ako nájsť mentora alebo sa ním stať.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Mentoring: čo to je a ako nájsť mentora | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Mentoring na GROWNi prepája mentees s mentormi z praxe. Zisti, čo je mentoring, ako sa líši od koučingu a ako nájsť mentora alebo sa ním stať.'
                }
            ],
            breadcrumbs: {
                title: 'Mentoring'
            }
        },
        {
            slugId: 'mentor-id',
            titleTemplate: '%s | Mentor | GROWNi.sk ',
            breadcrumbs: {
                title: '%s',
                path: ['Mentoring', 'Mentori']
            }
        },
        {
            slugId: 'dobrovolnictvo',
            title: 'Dobrovoľníctvo na Slovensku: zapoj sa a pomáhaj | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Dobrovoľnícke ponuky v neziskovkách, komunitách aj vo verejnom sektore – v Bratislave, Košiciach aj online. Zapoj sa alebo nájdi dobrovoľníkov.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Dobrovoľníctvo na Slovensku: zapoj sa a pomáhaj | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Dobrovoľnícke ponuky v neziskovkách, komunitách aj vo verejnom sektore – v Bratislave, Košiciach aj online. Zapoj sa alebo nájdi dobrovoľníkov.'
                }
            ],
            breadcrumbs: {
                title: 'Zoznam dobrovoľníctva'
            }
        },
        {
            slugId: 'tyzdendobrovolnictva',
            title: 'Týždeň dobrovoľníctva | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: 'Týždeň dobrovoľníctva je celoslovenská kampaň, ktorá oslavuje dobrovoľníctvo. Vyskúšaj si ho, spoznaj výnimočných ľudí a pomôž tam, kde je to potrebné.'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: 'Týždeň dobrovoľníctva je celoslovenská kampaň, ktorá oslavuje dobrovoľníctvo. Vyskúšaj si ho, spoznaj výnimočných ľudí a pomôž tam, kde je to potrebné.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Týždeň dobrovoľníctva | GROWNi.sk'
                }
            ],
            breadcrumbs: {
                title: 'Týždeň dobrovoľníctva'
            }
        },
        {
            slugId: 'alumni-braingain',
            title: 'Slovakia Reconnected | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: 'Exkluzívne 2 eventy pre alumni letných škôl Klimatická Akadémia, Škola diplomacie a PMU. Networking 28.8. + co-design workshop 29.–30.8.2026 v Bratislave a okolí. Plne hradené pre účastníkov.'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: 'Pripoj sa k alumni letných škôl KA, ŠD, PMU a LEAF. Networking + co-design víkend, plne hradené z grantu ZSE.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Slovakia Reconnected | GROWNi.sk'
                }
            ],
            breadcrumbs: {
                title: 'Slovakia Reconnected'
            }
        },
        {
            slugId: 'dobrovolnictvo-dobrovolnicke-projekty',
            title: 'Dobrovoľnícke ponuky a projekty | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Dobrovoľnícka práca v neziskovkách a komunitách po celom Slovensku aj online. Vyber si projekt podľa kraja, témy či času a prihlás sa priamo cez GROWNi.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Dobrovoľnícke ponuky a projekty | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Dobrovoľnícka práca v neziskovkách a komunitách po celom Slovensku aj online. Vyber si projekt podľa kraja, témy či času a prihlás sa priamo cez GROWNi.'
                }
            ],
            breadcrumbs: {
                title: 'Príležitosti',
                path: ['Dobrovoľníctvo']
            }
        },
        {
            slugId: 'dobrovolnictvo-dobrovolnicke-projekty-id',
            titleTemplate: '%s | GROWNi.sk',
            breadcrumbs: {
                title: '%s',
                path: ['Dobrovoľníctvo', 'Príležitosti']
            }
        },
        {
            slugId: 'dobrovolnictvo-dobrovolnici',
            title: 'Nájdi dobrovoľníkov pre svoju organizáciu | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Dobrovoľníci, ktorí chcú pomáhať neziskovkám, komunitám aj verejnému sektoru. Filtruj podľa záujmov, kraja a času a osloviť ich môžeš priamo cez GROWNi.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Nájdi dobrovoľníkov pre svoju organizáciu | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Dobrovoľníci, ktorí chcú pomáhať neziskovkám, komunitám aj verejnému sektoru. Filtruj podľa záujmov, kraja a času a osloviť ich môžeš priamo cez GROWNi.'
                }
            ],
            breadcrumbs: {
                title: 'Dobrovoľníci',
                path: ['Dobrovoľníctvo']
            }
        },
        {
            slugId: 'dobrovolnictvo-dobrovolnici-id',
            titleTemplate: '%s | Dobrovoľník | GROWNi.sk',
            breadcrumbs: {
                title: '%s',
                path: ['Dobrovoľníctvo', 'Dobrovoľníci']
            }
        },
        {
            slugId: 'praca',
            title: 'Práca, pracovné ponuky a stáže so zmyslom | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Pracovné ponuky a stáže v neziskovkách, firmách aj vo verejnom sektore. Práca pre študentov, absolventov aj skúsených – prihlás sa priamo cez GROWNi.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Práca, pracovné ponuky a stáže so zmyslom | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Pracovné ponuky a stáže v neziskovkách, firmách aj vo verejnom sektore. Práca pre študentov, absolventov aj skúsených – prihlás sa priamo cez GROWNi.'
                }
            ],
            breadcrumbs: {
                title: 'Pracovné príležitosti'
            }
        },
        {
            slugId: 'praca-slug',
            titleTemplate: '%s | GROWNi.sk',
            breadcrumbs: {
                title: '%s',
                path: ['Pracovné príležitosti']
            }
        },
        {
            slugId: 'organizacie',
            title: 'Neziskové organizácie, firmy a iniciatívy | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Neziskové organizácie, školy, firmy aj inštitúcie verejného sektora na GROWNi. Pozri si ich pracovné ponuky, dobrovoľnícke projekty a vzdelávanie.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Neziskové organizácie, firmy a iniciatívy | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Neziskové organizácie, školy, firmy aj inštitúcie verejného sektora na GROWNi. Pozri si ich pracovné ponuky, dobrovoľnícke projekty a vzdelávanie.'
                }
            ],
            breadcrumbs: {
                title: 'Organizácie'
            }
        },
        {
            slugId: 'orgs-id',
            titleTemplate: '%s | GROWNi.sk',
            breadcrumbs: {
                title: '%s',
                path: ['Organizácie']
            }
        },
        {
            slugId: 'blog',
            title: 'Blogová sekcia projektu GROWNi.sk',
            breadcrumbs: {
                title: 'Blog'
            }
        },
        {
            slugId: 'blog-id',
            titleTemplate: '%s | GROWNi.sk',
            breadcrumbs: {
                title: '%s',
                path: ['Blog']
            }
        },
        {
            slugId: 'diskusie',
            title: 'Diskusie o mentoringu, práci, dobrovoľníctve a vzdelávaní | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Pýtaj sa a raď v komunite GROWNi: mentoring, práca a stáže, dobrovoľníctvo, vzdelávanie a sebapoznanie.'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Pýtaj sa a raď v komunite GROWNi: mentoring, práca a stáže, dobrovoľníctvo, vzdelávanie a sebapoznanie.'
                },
                { hid: 'og:title', property: 'og:title', content: 'Diskusie | GROWNi.sk' }
            ],
            breadcrumbs: {
                title: 'Diskusie'
            }
        },
        {
            // Title and description come from the topic (`pages/diskusie/[topic]/index.vue`).
            slugId: 'diskusie-topic',
            titleTemplate: '%s | GROWNi.sk',
            meta: [],
            breadcrumbs: {
                title: '%s',
                path: ['Diskusie']
            }
        },
        {
            slugId: 'notifikacie-odber',
            title: 'Odber noviniek e-mailom | GROWNi.sk',
            meta: [{ hid: 'robots', name: 'robots', content: 'noindex, nofollow' }]
        },
        {
            slugId: 'notifikacie-emaily',
            title: 'E-maily o odpovediach a komentároch | GROWNi.sk',
            meta: [{ hid: 'robots', name: 'robots', content: 'noindex, nofollow' }]
        },
        {
            slugId: 'diskusie-nova',
            title: 'Nová diskusia | GROWNi.sk',
            meta: [{ hid: 'robots', name: 'robots', content: 'noindex, follow' }],
            breadcrumbs: {
                title: 'Nová diskusia',
                path: ['Diskusie']
            }
        },
        {
            slugId: 'contakt',
            title: 'Kontaktné informácie portálu GROWNi.sk',
            breadcrumbs: {
                title: 'Kontakt'
            }
        },
        {
            slugId: 'about',
            title: 'Kto sme a o čo sa snažíme na portáli GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'GROWNi.sk prepája ľudí s mentormi, dobrovoľníctvom, prácou, stážami a vzdelávaním. Pre študentov aj skúsených profesionálov, pre firmy aj neziskovky.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Kto sme a o čo sa snažíme na portáli GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'GROWNi.sk prepája ľudí s mentormi, dobrovoľníctvom, prácou, stážami a vzdelávaním. Pre študentov aj skúsených profesionálov, pre firmy aj neziskovky.'
                }
            ],
            breadcrumbs: {
                title: 'O nás'
            }
        },
        {
            slugId: 'media',
            title: 'Media kit – logo, farby a kontakt pre médiá | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Logo GROWNi v SVG a PNG, farby, písmo, popis platformy a kontakt pre médiá. Všetko na stiahnutie v jednom balíku.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Media kit – logo, farby a kontakt pre médiá | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Logo GROWNi v SVG a PNG, farby, písmo, popis platformy a kontakt pre médiá. Všetko na stiahnutie v jednom balíku.'
                }
            ],
            breadcrumbs: {
                title: 'Pre médiá'
            }
        },
        {
            slugId: 'profile',
            title: 'Životopis | GROWNi.sk',
            breadcrumbs: {
                title: 'Životopis',
                path: ['Administrácia']
            }
        },
        {
            slugId: 'profile-edit',
            breadcrumbs: {
                title: 'Nastavenia',
                path: ['Administrácia', 'Životopis']
            }
        },
        {
            slugId: 'profile-welcome',
            breadcrumbs: {
                title: 'Administrácia'
            }
        },
        {
            slugId: 'profile-activity',
            title: 'Žiadosti a aktivity | GROWNi.sk',
            breadcrumbs: {
                title: 'Žiadosti a aktivity',
                path: ['Administrácia']
            }
        },
        {
            slugId: 'talentbase-kandidati-jobId',
            title: 'Vhodní kandidáti z TalentBase | GROWNi.sk',
            meta: [{ hid: 'robots', name: 'robots', content: 'noindex, nofollow' }],
            breadcrumbs: {
                title: 'Vhodní kandidáti',
                path: ['Administrácia', 'Žiadosti a aktivity']
            }
        },
        {
            slugId: 'profile-orgs-create',
            breadcrumbs: {
                title: 'Vytvoriť organizáciu',
                path: ['Administrácia']
            }
        },
        {
            slugId: 'messages',
            breadcrumbs: {
                title: 'Správy'
            }
        },
        {
            slugId: 'profile-notifications',
            title: 'Notifikácie | GROWNi.sk',
            breadcrumbs: {
                title: 'Notifikácie'
            }
        },
        {
            slugId: 'chats',
            title: 'Správy | GROWNi.sk',
            breadcrumbs: {
                title: 'Správy'
            }
        },
        {
            // English on purpose: VIBE is the international mentoring community
            slugId: 'vibe',
            title: 'VIBE – online mentoring community | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'VIBE is an online mentoring community where you can find and connect with more experienced people in what you are interested in.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'VIBE – online mentoring community | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'VIBE is an online mentoring community where you can find and connect with more experienced people in what you are interested in.'
                }
            ],
            breadcrumbs: {
                title: 'VIBE'
            }
        },
        {
            slugId: 'verejny-sektor',
            title: 'Verejný sektor: práca, dobrovoľníctvo, mentoring | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Verejný sektor potrebuje kvalitných ľudí. Nájdi prácu, stáž či dobrovoľníctvo vo verejnej správe a pomôž tvoriť krajinu, v ktorej sa oplatí žiť.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Verejný sektor: práca, dobrovoľníctvo, mentoring | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Verejný sektor potrebuje kvalitných ľudí. Nájdi prácu, stáž či dobrovoľníctvo vo verejnej správe a pomôž tvoriť krajinu, v ktorej sa oplatí žiť.'
                }
            ],
            breadcrumbs: {
                title: 'Verejný sektor'
            }
        },
        {
            slugId: 'verejny-sektor-praca-a-staze',
            title: 'Práca a stáže vo verejnom sektore | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Pracovné ponuky a stáže v štátnej správe, samospráve a verejných inštitúciách. Nájdi prácu, ktorou môžeš zlepšovať Slovensko, a prihlás sa cez GROWNi.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Práca a stáže vo verejnom sektore | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Pracovné ponuky a stáže v štátnej správe, samospráve a verejných inštitúciách. Nájdi prácu, ktorou môžeš zlepšovať Slovensko, a prihlás sa cez GROWNi.'
                }
            ],
            breadcrumbs: {
                title: 'Práca a stáže',
                path: ['Verejný sektor']
            }
        },
        {
            slugId: 'verejny-sektor-dobrovolnicke-projekty',
            title: 'Dobrovoľníctvo vo verejnom sektore | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Dobrovoľnícke príležitosti v štátnej správe, samospráve a verejných inštitúciách. Zapoj sa a pomôž zlepšovať verejné služby na Slovensku.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Dobrovoľníctvo vo verejnom sektore | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Dobrovoľnícke príležitosti v štátnej správe, samospráve a verejných inštitúciách. Zapoj sa a pomôž zlepšovať verejné služby na Slovensku.'
                }
            ],
            breadcrumbs: {
                title: 'Dobrovoľnícke projekty',
                path: ['Verejný sektor']
            }
        },
        {
            slugId: 'staze-slsp',
            title: 'Stáže pre vysokoškolákov | GROWNi.sk & Nadácia Slovenskej sporiteľne',
            breadcrumbs: {
                title: 'Stáže pre vysokoškolákov'
            }
        },
        {
            slugId: 'praca-index',
            title: 'Pracovné ponuky a stáže | GROWNi.sk',
            breadcrumbs: {
                title: 'Pracovné príležitosti'
            }
        },
        {
            slugId: 'vzdelavanie-index',
            title: 'Webináre, eventy a workshopy | GROWNi.sk',
            breadcrumbs: {
                title: 'Vzdelávanie'
            }
        },
        {
            slugId: 'cennik',
            title: 'Cenník partnerských riešení | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Cenník partnerských riešení GROWNi pre firmy a startupy, verejný sektor aj neziskovky a školy. Vyberte si ponuku podľa typu vašej organizácie.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Cenník partnerských riešení | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Cenník partnerských riešení GROWNi pre firmy a startupy, verejný sektor aj neziskovky a školy. Vyberte si ponuku podľa typu vašej organizácie.'
                }
            ],
            breadcrumbs: {
                title: 'Cenník'
            }
        },
        {
            slugId: 'pre-partnerov',
            title: 'Partnerské riešenia pre firmy a startupy | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'GROWNi pomáha rozvíjať hodnotovo orientovaných ľudí, firmy a organizácie. Pozrite si partnerské riešenia a služby pre firmy, verejný sektor aj neziskovky.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Partnerské riešenia pre firmy a startupy | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'GROWNi pomáha rozvíjať hodnotovo orientovaných ľudí, firmy a organizácie. Pozrite si partnerské riešenia a služby pre firmy, verejný sektor aj neziskovky.'
                }
            ],
            breadcrumbs: {
                title: 'Pre partnerov'
            }
        },
        {
            slugId: 'pre-partnerov-verejny-sektor',
            title: 'Partnerské riešenia pre verejný sektor | GROWNi.sk',
            breadcrumbs: {
                title: 'Pre verejný sektor'
            }
        },
        {
            slugId: 'pre-neziskovky',
            title: 'Riešenia pre neziskovky a školy | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Robíš v nezisku? Na GROWNi nájdeš dobrovoľníkov, mentorov aj kolegov, inšpiráciu od iných organizácií a priestor zviditeľniť svoju prácu. Zadarmo.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Riešenia pre neziskovky a školy | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Robíš v nezisku? Na GROWNi nájdeš dobrovoľníkov, mentorov aj kolegov, inšpiráciu od iných organizácií a priestor zviditeľniť svoju prácu. Zadarmo.'
                }
            ],
            breadcrumbs: {
                title: 'Pre neziskovky'
            }
        },
        {
            slugId: 'pribehy',
            title: 'Príbehy | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: 'Príbehy ľudí a organizácií, ktorým GROWNi pomohlo nájsť mentora, stáž, nových kolegov či dobrovoľníkov.'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: 'Príbehy ľudí a organizácií, ktorým GROWNi pomohlo nájsť mentora, stáž, nových kolegov či dobrovoľníkov.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Príbehy | GROWNi.sk'
                }
            ],
            breadcrumbs: {
                title: 'Príbehy'
            }
        },
        {
            slugId: 'prestredoskolakov',
            title: 'Príležitosti pre stredoškolákov | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: 'Mentoring, dobrovoľníctvo, stáže a vzdelávanie pre stredoškolákov. Rozvíjaj svoj talent a priprav sa na vysokú školu alebo kariéru.'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: 'Mentoring, dobrovoľníctvo, stáže a vzdelávanie pre stredoškolákov. Rozvíjaj svoj talent a priprav sa na vysokú školu alebo kariéru.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Príležitosti pre stredoškolákov | GROWNi.sk'
                }
            ],
            breadcrumbs: {
                title: 'Pre stredoškolákov'
            }
        },
        {
            slugId: 'competition_summer_school',
            title: 'Future of Markets — Competition Summer School 2026 | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: 'Letná škola o hospodárskej súťaži a regulácii trhov. 31.8.–3.9.2026 v priestoroch Protimonopolného úradu SR v Bratislave. Organizuje LEAF a PMÚ SR.'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: 'Letná škola o hospodárskej súťaži a regulácii trhov. 31.8.–3.9.2026 v priestoroch PMÚ SR v Bratislave.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Future of Markets — Competition Summer School 2026 | GROWNi.sk'
                }
            ],
            breadcrumbs: {
                title: 'Future of Markets'
            }
        },
        {
            slugId: 'staze-pre-firmy',
            title: 'Stáže pre firmy — získajte najlepších stážistov z univerzít | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: 'Obsaďte stážistické pozície rýchlejšie a lacnejšie. GROWNi prepája firmy s 3 200+ motivovanými vysokoškolákmi v IT, marketingu, práve, financiách a ďalších sektoroch.'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: 'Obsaďte stážistické pozície rýchlejšie a lacnejšie. GROWNi prepája firmy s 3 200+ motivovanými vysokoškolákmi.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Stáže pre firmy — získajte najlepších stážistov z univerzít | GROWNi.sk'
                }
            ],
            breadcrumbs: {
                title: 'Stáže pre firmy'
            }
        },
        {
            slugId: 'staze-pre-studentov',
            title: 'Stáž počas štúdia: nájdeme ti ju v overenej firme | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: 'Hľadáš stáž počas štúdia? Povedz nám, o akú oblasť máš záujem, a my ti nájdeme stáž v overenej firme. IT, marketing, financie, právo a ďalšie.'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: 'Hľadáš stáž počas štúdia? Povedz nám, o akú oblasť máš záujem, a my ti nájdeme stáž v overenej firme.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Stáž počas štúdia: nájdeme ti ju v overenej firme | GROWNi.sk'
                }
            ],
            breadcrumbs: {
                title: 'Stáže pre študentov'
            }
        },
        {
            slugId: '2-percenta-z-dane',
            title: 'Daruj nám svoje 2% z dane | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Podpor GROWNi darovaním 2% (3%) z dane. Pomôž nám budovať lepšie Slovensko. Tu nájdeš postup pre zamestnancov, fyzické aj právnické osoby.'
                }
            ],
            breadcrumbs: {
                title: '2% z dane'
            }
        },
        {
            slugId: 'newsletter',
            title: 'Newsletter | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Prihlás sa na odber GROWNi newslettera. Novinky, príležitosti a inšpirácia pre jednotlivcov, firmy aj organizácie, ktorí chcú rásť. Mentoring, práca, dobrovoľníctvo a vzdelávanie priamo do schránky.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Newsletter | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Prihlás sa na odber GROWNi newslettera. Novinky, príležitosti a inšpirácia pre jednotlivcov, firmy aj organizácie, ktorí chcú rásť. Mentoring, práca, dobrovoľníctvo a vzdelávanie priamo do schránky.'
                }
            ],
            breadcrumbs: {
                title: 'Newsletter'
            }
        },
        {
            slugId: 'ako-najst-dobrovolnika',
            title: 'Ako nájsť dobrovoľníkov pre neziskovku | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Nájdi dobrovoľníkov pre neziskovku, komunitnú iniciatívu, študentský projekt či sociálny podnik. Stretni ľudí, ktorí chcú venovať svoj čas dobrej veci.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Ako nájsť dobrovoľníkov pre neziskovku | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Nájdi dobrovoľníkov pre neziskovku, komunitnú iniciatívu, študentský projekt či sociálny podnik. Stretni ľudí, ktorí chcú venovať svoj čas dobrej veci.'
                }
            ],
            breadcrumbs: {
                title: 'Ako nájsť dobrovoľníka'
            }
        },
        {
            slugId: 'chcem-byt-dobrovolnik',
            title: 'Ako sa stať dobrovoľníkom | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Daruj čas, talent a energiu. Vytvor si dobrovoľnícky profil, vyber si príležitosť online či naživo, krátkodobú aj dlhodobú, a prispej k pozitívnej zmene.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Ako sa stať dobrovoľníkom | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Daruj čas, talent a energiu. Vytvor si dobrovoľnícky profil, vyber si príležitosť online či naživo, krátkodobú aj dlhodobú, a prispej k pozitívnej zmene.'
                }
            ],
            breadcrumbs: {
                title: 'Chcem byť dobrovoľník'
            }
        },
        {
            slugId: 'ako-pridat-pracovnu-ponuku',
            title: 'Ako zverejniť pracovnú ponuku | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Zverejni pracovnú ponuku či stáž na GROWNi: zaregistruj sa, vytvor profil organizácie a pridaj pozíciu. Celý postup ti ukážeme v 4-minútovom videu.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Ako zverejniť pracovnú ponuku | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Zverejni pracovnú ponuku či stáž na GROWNi: zaregistruj sa, vytvor profil organizácie a pridaj pozíciu. Celý postup ti ukážeme v 4-minútovom videu.'
                }
            ],
            breadcrumbs: {
                title: 'Ako pridať pracovnú ponuku'
            }
        },
        {
            slugId: 'pracovne-skupiny',
            title: 'GROWps – pracovné skupiny pre výmenu skúseností | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'GROWps sú uzavreté pracovné skupiny vedené ambasádorom, expertom v danej oblasti. Na pravidelných stretnutiach si vymieňaš skúsenosti s ľuďmi ako ty.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'GROWps – pracovné skupiny pre výmenu skúseností | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'GROWps sú uzavreté pracovné skupiny vedené ambasádorom, expertom v danej oblasti. Na pravidelných stretnutiach si vymieňaš skúsenosti s ľuďmi ako ty.'
                }
            ],
            breadcrumbs: {
                title: 'Pracovné skupiny'
            }
        },
        {
            slugId: 'slovaci-v-zahranici',
            title: 'Slováci v zahraničí – pomôž rozvoju Slovenska | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Prepájame skúsených Slovákov v zahraničí so startupmi, neziskovkami a verejným sektorom na Slovensku cez mentoring, konzultácie či expertné dobrovoľníctvo.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Slováci v zahraničí – pomôž rozvoju Slovenska | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Prepájame skúsených Slovákov v zahraničí so startupmi, neziskovkami a verejným sektorom na Slovensku cez mentoring, konzultácie či expertné dobrovoľníctvo.'
                }
            ],
            breadcrumbs: {
                title: 'Slováci v zahraničí'
            }
        },
        {
            slugId: 'projekty',
            title: 'Zrealizuj svoj projekt s návodmi a mentormi | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Máš nápad alebo rozbehnutý projekt? GROWNi Projekty ti ponúknu návody od garantov obsahu, odporúčania mentorov a organizácie, kde ho môžeš rozvíjať.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Zrealizuj svoj projekt s návodmi a mentormi | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Máš nápad alebo rozbehnutý projekt? GROWNi Projekty ti ponúknu návody od garantov obsahu, odporúčania mentorov a organizácie, kde ho môžeš rozvíjať.'
                }
            ],
            breadcrumbs: {
                title: 'Projekty'
            }
        },
        {
            slugId: 'linky',
            title: 'Linky | GROWNi.sk',
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content:
                        'Všetky dôležité odkazy GROWNi na jednom mieste. Mentoring, dobrovoľníctvo, práca a stáže, vzdelávanie aj sebapoznanie.'
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: 'Linky | GROWNi.sk'
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content:
                        'Všetky dôležité odkazy GROWNi na jednom mieste. Mentoring, dobrovoľníctvo, práca a stáže, vzdelávanie aj sebapoznanie.'
                }
            ],
            breadcrumbs: {
                title: 'Linky'
            }
        }
    ]
}
